import constants, { BOOTBOX_STATUS } from '../../lib/constants'
import Bill, { BillFromJson } from './Bill'
import Citation, { CitationFromJson } from './Citation'
import License, { LicenseFromJson } from './License'
import CoordinatesLocation from './CoordinatesLocation'
import Operation from './Operation'

interface CaseFromJson {
  additional_information: string
  booted_location: CoordinatesLocation
  booted_location_address: string
  booted_location_note: string
  booter_id: number
  citations: CitationFromJson[]
  driver_id: number|null
  bill: BillFromJson|null
  id: number
  images: string[]|null
  license: LicenseFromJson
  operation_id: number
  operation: Operation|null
  operation_variables: any
  releaser_id: number|null
  status_id: string
  vehicle_id: number
  wheel_clamp_id: number
  tow_requester_id: number|null
}

class Case {
  additionalInformation: string
  bootedLocation: CoordinatesLocation
  bootedLocationAddress: string
  bootedLocationNote: string
  booterId: number
  citations: Citation[]
  driverId: number|null
  bill: Bill|null
  id: number
  images: string[]|null
  license: License
  operationId: number
  operation: Operation|null
  operationVariables: any
  releaserId: number|null
  status: string
  vehicleId: number
  wheelClampId: number
  towRequesterId: number|null

  constructor (
    additionalInformation: string,
    bootedLocation: CoordinatesLocation,
    bootedLocationAddress: string,
    bootedLocationNote: string,
    booterId: number,
    citations: Citation[],
    driverId: number|null,
    bill: Bill|null,
    id: number,
    images: string[]|null,
    license: License,
    operationId: number,
    operation: Operation|null,
    operationVariables: any,
    releaserId: number|null,
    status: string,
    vehicleId: number,
    wheelClampId: number,
    towRequesterId: number|null
  ) {
    this.additionalInformation = additionalInformation
    this.bootedLocation = bootedLocation
    this.bootedLocationAddress = bootedLocationAddress
    this.bootedLocationNote = bootedLocationNote
    this.booterId = booterId
    this.citations = citations
    this.driverId = driverId
    this.bill = bill
    this.id = id
    this.images = images
    this.license = license
    this.operationId = operationId
    this.operation = operation
    this.operationVariables = operationVariables
    this.releaserId = releaserId
    this.status = status
    this.vehicleId = vehicleId
    this.wheelClampId = wheelClampId
    this.towRequesterId = towRequesterId
  }

  get slug () {
    return `${this.id}-${this.license.state}-${this.license.number}`
  }

  get assistedReleaseCost () {
    return this.operationVariables?.assisted_release_cost
  }

  get towingFee () {
    return this.operationVariables.towing_fee
  }

  get minutesToAssistRelease () {
    return this.operationVariables.minutesToAssistRelease
  }

  get isCreated () {
    return this.status === constants.caseStatusCodes.Created
  }

  get isBooted () {
    return this.status === constants.caseStatusCodes.Booted
  }

  get isSolved () {
    return this.status === constants.caseStatusCodes.Solved
  }

  get isTowed () {
    return this.status === constants.caseStatusCodes.Towed
  }

  get isSelfRelease () {
    return this.status === constants.caseStatusCodes['Self release']
  }

  get isAssistedRelease () {
    return this.status === constants.caseStatusCodes['Assisted release']
  }

  get isBootboxed () {
    return this.status === BOOTBOX_STATUS
  }

  get isPaid () {
    return this.isSelfRelease || this.isAssistedRelease || this.isSolved || this.isCash || this.isBootboxed
  }

  get isCash () {
    return this.status === constants.caseStatusCodes['Cash Payment Requested']
  }

  get hasCashPayment () {
    return this.operation?.variables?.accept_cash_payments === true
  }

  static fromJson ({
    additional_information,
    booted_location,
    booted_location_address,
    booted_location_note,
    booter_id,
    citations = [],
    driver_id,
    bill,
    id,
    images,
    license,
    operation_id,
    operation,
    operation_variables,
    releaser_id,
    status_id,
    vehicle_id,
    wheel_clamp_id,
    tow_requester_id
  }: CaseFromJson) {
    return new Case(
      additional_information,
      booted_location,
      booted_location_note,
      booted_location_address,
      booter_id,
      citations.map((citation: CitationFromJson) => Citation.fromJson(citation)),
      driver_id,
      bill ? Bill.fromJson(bill) : null,
      id,
      images,
      License.fromJson(license),
      operation_id,
      operation,
      operation_variables,
      releaser_id,
      status_id,
      vehicle_id,
      wheel_clamp_id,
      tow_requester_id
    )
  }
}

export default Case
